import { Component, OnInit, PLATFORM_ID, TransferState, inject, makeStateKey, ChangeDetectionStrategy } from '@angular/core';
import { AsyncPipe, NgClass, DatePipe, isPlatformServer } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { map } from 'rxjs';
import { AnnouncementsService } from '../../core/announcements/announcements.service';
import { Announcement } from '../../core/api/modules/announcements';

/** Carries the server-fetched announcement list into the browser so hydration doesn't refetch. */
export const STATUS_ANNOUNCEMENTS_KEY = makeStateKey<Announcement[]>('status-announcements');

@Component({
  selector: 'app-status',
  standalone: true,
  imports: [AsyncPipe, NgClass, DatePipe, MatIconModule, TranslateModule],
  templateUrl: './status.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './status.component.css',
})
export class StatusComponent implements OnInit {
  private readonly service = inject(AnnouncementsService);
  private readonly transferState = inject(TransferState);
  private readonly platformId = inject(PLATFORM_ID);

  readonly announcements$ = this.service.announcements$.pipe(
    map(list => {
      const now = new Date();
      return list.filter(a => !a.startsAt || new Date(a.startsAt) <= now);
    })
  );

  ngOnInit(): void {
    if (isPlatformServer(this.platformId)) {
      this.service.fetchOnce().subscribe(list => {
        this.service.seed(list);
        this.transferState.set(STATUS_ANNOUNCEMENTS_KEY, list);
      });
      return;
    }

    const hadTransferState = this.transferState.hasKey(STATUS_ANNOUNCEMENTS_KEY);
    if (hadTransferState) {
      this.service.seed(this.transferState.get(STATUS_ANNOUNCEMENTS_KEY, []));
      this.transferState.remove(STATUS_ANNOUNCEMENTS_KEY);
    }

    this.service.startPolling({ skipInitialFetch: hadTransferState });
  }

  iconFor(severity: string): string {
    if (severity === 'critical') return 'error';
    if (severity === 'warning') return 'warning';
    return 'info';
  }
}
